import React from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangle, ChevronRight } from "lucide-react";
import { formatINR } from "../../utils/formatINR";
import { formatDay } from "../../utils/finance";
import { chartColors, neutral } from "../../theme/palette";

const Th = ({ children, right }) => (
  <th className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
    {children}
  </th>
);

const Count = ({ n, tone }) => (
  <span className={`tabular-nums ${n ? tone : "text-neutral-300"}`}>{n}</span>
);

export function GroupSettlements({ groups, onOpenGroup }) {
  const rows = groups.filter((g) => g.count > 0 || g.pending_now > 0);
  return (
    <section className="card overflow-hidden">
      <div className="border-b border-neutral-200 px-4 py-4 sm:px-5">
        <h2 className="section-title">Settlements by group</h2>
        <p className="mt-0.5 text-[13px] text-neutral-500">Money paid out to each group in this period. Click a group for its settlement history.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-[13px]">
          <thead className="bg-neutral-50">
            <tr className="border-b border-neutral-200">
              <Th>Group</Th>
              <Th right>Paid in period</Th>
              <Th right>Settled</Th>
              <Th right>Pending</Th>
              <Th right>Failed</Th>
              <Th right>Outstanding now</Th>
              <Th>Latest payout</Th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {rows.map((g) => (
              <tr key={g.group_id} className="cursor-pointer hover:bg-neutral-50" onClick={() => onOpenGroup(g.group_id)}>
                <td className="px-3 py-2.5">
                  <div className="font-medium text-ink">{g.group_name}</div>
                  <div className="text-[11px] text-neutral-500">
                    {g.count} record{g.count === 1 ? "" : "s"}
                    {g.manual_count > 0 && ` · ${g.manual_count} manual`}
                  </div>
                  {g.both_methods && (
                    <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-amber-700">
                      <AlertTriangle className="h-3 w-3" /> Paid both automatically and manually: check for double payment
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-red-700">
                  {formatINR(g.total_paid)}
                  {g.auto_paid > 0 && g.manual_paid > 0 && (
                    <div className="text-[11px] font-normal text-neutral-500">
                      {formatINR(g.auto_paid)} auto · {formatINR(g.manual_paid)} manual
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right"><Count n={g.settled + g.manual_count} tone="text-green-700" /></td>
                <td className="px-3 py-2.5 text-right"><Count n={g.pending} tone="text-amber-700" /></td>
                <td className="px-3 py-2.5 text-right"><Count n={g.failed} tone="text-red-700" /></td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {g.pending_now > 0 ? (
                    <span className="text-amber-700">
                      {formatINR(g.pending_now)}
                      <span className="block text-[11px] text-neutral-500">{g.pending_now_count} settlement{g.pending_now_count === 1 ? "" : "s"}</span>
                    </span>
                  ) : (
                    <span className="text-neutral-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap text-neutral-600">{g.latest_date ? formatDay(g.latest_date) : "—"}</td>
                <td className="pr-3 text-neutral-400"><ChevronRight className="h-4 w-4" /></td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-neutral-500">No settlements in this period.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const shortDay = new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", timeZone: "UTC" });

export function RefundAnalysis({ summary, days, onOpen }) {
  const by = summary?.by_status || {};
  const buckets = [
    { key: "processed", label: "Processed", tone: "text-ink", note: "Deducted from Tetra retained" },
    { key: "pending", label: "Pending", tone: "text-amber-700", note: "Accepted by Razorpay, not yet paid" },
    { key: "failed", label: "Failed", tone: "text-red-700", note: "Not paid to the customer" },
  ];
  const trend = [...days]
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .map((d) => ({ day: shortDay.format(new Date(`${d.date}T00:00:00Z`)), processed: d.refunds, pending: d.refunds_pending }));
  const anyRefunds = trend.some((t) => t.processed || t.pending);

  return (
    <section className="card overflow-hidden">
      <div className="flex items-start justify-between gap-3 border-b border-neutral-200 px-4 py-4 sm:px-5">
        <div>
          <h2 className="section-title">Refunds</h2>
          <p className="mt-0.5 text-[13px] text-neutral-500">From Razorpay. Only processed refunds count as money paid back.</p>
        </div>
        <button type="button" onClick={onOpen} className="btn btn-secondary text-[13px]" disabled={!summary?.count}>
          View refunds
        </button>
      </div>
      <div className="grid grid-cols-3 divide-x divide-neutral-100 border-b border-neutral-100">
        {buckets.map((b) => (
          <div key={b.key} className="px-4 py-3">
            <p className="text-[12px] font-medium text-neutral-500">{b.label}</p>
            <p className={`mt-0.5 text-lg font-semibold tabular-nums ${b.tone}`}>{formatINR(by[b.key]?.amount || 0)}</p>
            <p className="text-[11px] text-neutral-500">
              {by[b.key]?.count || 0} refund{(by[b.key]?.count || 0) === 1 ? "" : "s"}
            </p>
            <p className="mt-1 hidden text-[11px] text-neutral-400 sm:block">{b.note}</p>
          </div>
        ))}
      </div>
      <div className="px-2 py-3 sm:px-4">
        {anyRefunds ? (
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={trend} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} stroke={neutral[200]} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: neutral[500] }} tickLine={false} axisLine={false} minTickGap={16} />
              <YAxis tick={{ fontSize: 11, fill: neutral[500] }} tickLine={false} axisLine={false} width={56} tickFormatter={(v) => `₹${v}`} />
              <Tooltip formatter={(v, name) => [formatINR(v), name === "processed" ? "Processed" : "Pending"]} cursor={{ fill: neutral[100] }} />
              <Bar dataKey="processed" stackId="r" fill={chartColors.danger} radius={[0, 0, 0, 0]} />
              <Bar dataKey="pending" stackId="r" fill={chartColors.warning} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="py-8 text-center text-[13px] text-neutral-500">No refunds in this period.</p>
        )}
      </div>
    </section>
  );
}
